import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { Upload, File, Image, FileText, Video, Music, Download, Share2, Trash2, Eye, Lock, Unlock, Folder, Plus, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { storage } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { doc, setDoc, getDoc, collection, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const DocumentManager = () => {
  const { user, language } = useApp();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sharing, setSharing] = useState(false);
  const fileInputRef = useRef(null);

  const translations = {
    english: {
      title: 'Document Manager',
      subtitle: 'Upload, organize, and share your legal documents',
      uploadFiles: 'Upload Files',
      dragDrop: 'Drag and drop files here or click to browse',
      supportedFormats: 'Supported formats: PDF, DOC, DOCX, JPG, PNG, MP4, MP3',
      maxFileSize: 'Max file size: 10MB',
      documents: 'Documents',
      images: 'Images',
      videos: 'Videos',
      audio: 'Audio',
      all: 'All',
      search: 'Search documents...',
      sortBy: 'Sort by',
      date: 'Date',
      name: 'Name',
      size: 'Size',
      type: 'Type',
      upload: 'Upload',
      cancel: 'Cancel',
      delete: 'Delete',
      share: 'Share',
      view: 'View',
      download: 'Download',
      rename: 'Rename',
      move: 'Move',
      copy: 'Copy',
      noDocuments: 'No documents found',
      uploadSuccess: 'Files uploaded successfully!',
      uploadError: 'Error uploading files',
      deleteSuccess: 'Document deleted successfully!',
      deleteError: 'Error deleting document',
      shareSuccess: 'Document shared successfully!',
      shareError: 'Error sharing document',
      fileTooLarge: 'File size exceeds 10MB limit',
      unsupportedFormat: 'Unsupported file format',
      processing: 'Processing...',
      uploading: 'Uploading...',
      shared: 'Shared',
      private: 'Private',
      public: 'Public',
      sharedWith: 'Shared with',
      permissions: 'Permissions',
      viewOnly: 'View Only',
      edit: 'Edit',
      admin: 'Admin'
    },
    hindi: {
      title: 'दस्तावेज़ प्रबंधक',
      subtitle: 'अपने कानूनी दस्तावेज़ अपलोड, व्यवस्थित और साझा करें',
      uploadFiles: 'फ़ाइलें अपलोड करें',
      dragDrop: 'फ़ाइलें यहाँ खींचें और छोड़ें या ब्राउज़ करने के लिए क्लिक करें',
      supportedFormats: 'समर्थित प्रारूप: PDF, DOC, DOCX, JPG, PNG, MP4, MP3',
      maxFileSize: 'अधिकतम फ़ाइल आकार: 10MB',
      documents: 'दस्तावेज़',
      images: 'छवियां',
      videos: 'वीडियो',
      audio: 'ऑडियो',
      all: 'सभी',
      search: 'दस्तावेज़ खोजें...',
      sortBy: 'क्रमबद्ध करें',
      date: 'तिथि',
      name: 'नाम',
      size: 'आकार',
      type: 'प्रकार',
      upload: 'अपलोड',
      cancel: 'रद्द करें',
      delete: 'हटाएं',
      share: 'साझा करें',
      view: 'देखें',
      download: 'डाउनलोड',
      rename: 'नाम बदलें',
      move: 'स्थानांतरित करें',
      copy: 'कॉपी',
      noDocuments: 'कोई दस्तावेज़ नहीं मिला',
      uploadSuccess: 'फ़ाइलें सफलतापूर्वक अपलोड की गईं!',
      uploadError: 'फ़ाइलें अपलोड करने में त्रुटि',
      deleteSuccess: 'दस्तावेज़ सफलतापूर्वक हटा दिया गया!',
      deleteError: 'दस्तावेज़ हटाने में त्रुटि',
      shareSuccess: 'दस्तावेज़ सफलतापूर्वक साझा किया गया!',
      shareError: 'दस्तावेज़ साझा करने में त्रुटि',
      fileTooLarge: 'फ़ाइल का आकार 10MB सीमा से अधिक है',
      unsupportedFormat: 'असमर्थित फ़ाइल प्रारूप',
      processing: 'प्रोसेसिंग...',
      uploading: 'अपलोड हो रहा है...',
      shared: 'साझा किया गया',
      private: 'निजी',
      public: 'सार्वजनिक',
      sharedWith: 'के साथ साझा किया गया',
      permissions: 'अनुमतियां',
      viewOnly: 'केवल देखें',
      edit: 'संपादित करें',
      admin: 'व्यवस्थापक'
    }
  };

  const t = translations[language] || translations.english;

  // File type configurations
  const fileTypes = {
    'application/pdf': { icon: <FileText className="w-6 h-6" />, color: 'text-red-500', type: 'document' },
    'application/msword': { icon: <FileText className="w-6 h-6" />, color: 'text-blue-500', type: 'document' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: <FileText className="w-6 h-6" />, color: 'text-blue-500', type: 'document' },
    'image/jpeg': { icon: <Image className="w-6 h-6" />, color: 'text-green-500', type: 'image' },
    'image/png': { icon: <Image className="w-6 h-6" />, color: 'text-green-500', type: 'image' },
    'image/gif': { icon: <Image className="w-6 h-6" />, color: 'text-green-500', type: 'image' },
    'video/mp4': { icon: <Video className="w-6 h-6" />, color: 'text-purple-500', type: 'video' },
    'video/avi': { icon: <Video className="w-6 h-6" />, color: 'text-purple-500', type: 'video' },
    'audio/mpeg': { icon: <Music className="w-6 h-6" />, color: 'text-orange-500', type: 'audio' },
    'audio/wav': { icon: <Music className="w-6 h-6" />, color: 'text-orange-500', type: 'audio' }
  };

  // Load documents on component mount
  useEffect(() => {
    if (user?.uid) {
      loadDocuments();
    }
  }, [user]);

  // Load documents from Firestore
  const loadDocuments = async () => {
    try {
      const q = query(
        collection(db, 'documents'),
        where('userId', '==', user.uid),
        orderBy('uploadedAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const docs = [];
        snapshot.forEach((doc) => {
          docs.push({ id: doc.id, ...doc.data() });
        });
        setDocuments(docs);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error(t.loadError);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: ${t.fileTooLarge}`);
        return false;
      }
      if (!fileTypes[file.type]) {
        toast.error(`${file.name}: ${t.unsupportedFormat}`);
        return false;
      }
      return true;
    });
    setSelectedFiles(validFiles);
  };

  // Upload files
  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadPromises = selectedFiles.map(async (file, index) => {
        const fileName = `${user.uid}/${Date.now()}_${file.name}`;
        const storageRef = ref(storage, fileName);
        
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        const documentData = {
          userId: user.uid,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          downloadURL,
          storagePath: fileName,
          uploadedAt: new Date().toISOString(),
          isPublic: false,
          sharedWith: [],
          tags: [],
          description: ''
        };

        await addDoc(collection(db, 'documents'), documentData);
        
        setUploadProgress(((index + 1) / selectedFiles.length) * 100);
      });

      await Promise.all(uploadPromises);
      
      toast.success(t.uploadSuccess);
      setSelectedFiles([]);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(t.uploadError);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete document
  const deleteDocument = async (document) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      // Delete from storage
      const storageRef = ref(storage, document.storagePath);
      await deleteObject(storageRef);
      
      // Delete from Firestore
      await deleteDoc(doc(db, 'documents', document.id));
      
      toast.success(t.deleteSuccess);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(t.deleteError);
    }
  };

  // Share document
  const shareDocument = async (document, email, permission = 'view') => {
    try {
      const updatedDoc = {
        ...document,
        sharedWith: [...(document.sharedWith || []), { email, permission, sharedAt: new Date().toISOString() }]
      };
      
      await updateDoc(doc(db, 'documents', document.id), updatedDoc);
      toast.success(t.shareSuccess);
    } catch (error) {
      console.error('Error sharing document:', error);
      toast.error(t.shareError);
    }
  };

  // Download document
  const downloadDocument = async (document) => {
    try {
      const response = await fetch(document.downloadURL);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = document.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Error downloading document');
    }
  };

  // Filter and sort documents
  const filteredDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.fileName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || fileTypes[doc.fileType]?.type === filterType;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.fileName.localeCompare(b.fileName);
        case 'size':
          return b.fileSize - a.fileSize;
        case 'type':
          return a.fileType.localeCompare(b.fileType);
        default:
          return new Date(b.uploadedAt) - new Date(a.uploadedAt);
      }
    });

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
              <p className="text-gray-600 mt-1">{t.subtitle}</p>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              {t.uploadFiles}
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t.search}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t.all}</option>
                <option value="document">{t.documents}</option>
                <option value="image">{t.images}</option>
                <option value="video">{t.videos}</option>
                <option value="audio">{t.audio}</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date">{t.date}</option>
                <option value="name">{t.name}</option>
                <option value="size">{t.size}</option>
                <option value="type">{t.type}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredDocuments.map((document) => (
            <motion.div
              key={document.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`${fileTypes[document.fileType]?.color || 'text-gray-500'}`}>
                  {fileTypes[document.fileType]?.icon || <File className="w-6 h-6" />}
                </div>
                <div className="flex items-center gap-1">
                  {document.isPublic ? (
                    <Unlock className="w-4 h-4 text-green-500" />
                  ) : (
                    <Lock className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
              
              <h3 className="font-medium text-gray-900 mb-1 truncate">{document.fileName}</h3>
              <p className="text-sm text-gray-500 mb-3">{formatFileSize(document.fileSize)}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {new Date(document.uploadedAt).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => downloadDocument(document)}
                    className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                    title={t.download}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSelectedDocument(document)}
                    className="p-1 text-gray-400 hover:text-green-500 transition-colors"
                    title={t.share}
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteDocument(document)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title={t.delete}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="text-center py-12">
            <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.noDocuments}</h3>
            <p className="text-gray-500">Upload your first document to get started</p>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{t.uploadFiles}</h2>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">{t.dragDrop}</p>
              <p className="text-sm text-gray-500 mb-4">{t.supportedFormats}</p>
              <p className="text-sm text-gray-500">{t.maxFileSize}</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mp3,.wav"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t.uploadFiles}
              </button>
            </div>

            {selectedFiles.length > 0 && (
              <div className="mb-4">
                <h3 className="font-medium mb-2">Selected Files:</h3>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploading && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">{t.uploading} {Math.round(uploadProgress)}%</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={uploading}
              >
                {t.cancel}
              </button>
              <button
                onClick={uploadFiles}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={uploading || selectedFiles.length === 0}
              >
                {uploading ? t.uploading : t.upload}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Share Document</h2>
            <p className="text-gray-600 mb-4">{selectedDocument.fileName}</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permission</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="view">{t.viewOnly}</option>
                  <option value="edit">{t.edit}</option>
                  <option value="admin">{t.admin}</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setSelectedDocument(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.cancel}
              </button>
              <button
                onClick={() => {
                  // Handle share logic
                  setSelectedDocument(null);
                }}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t.share}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentManager;
